import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@libs/zapi-ui/shadcn";
import { AppButton } from "@libs/zapi-ui/custom";
import { ArrowDown, ArrowUp, ArrowsDownUp } from "@phosphor-icons/react";
import type { TOrganizationProjectDimension } from "@/zapi-frontend/shared/types/projects";
import type { TPortfolioSortDir } from "../../lib/portfolioStorage";
import { sortFieldLabel, sortOrderOptions } from "../../lib/sortOptions";
import { SortBySection } from "./_SortBySection";
import { useTranslation } from "react-i18next";

type TProps = {
  sortFieldKey: string;
  sortDir: TPortfolioSortDir;
  dimensions: TOrganizationProjectDimension[];
  onSortFieldChange: (key: string) => void;
  onSortDirChange: (dir: TPortfolioSortDir) => void;
};

export function SortPopover(props: TProps) {
  const { sortFieldKey, sortDir, dimensions, onSortFieldChange, onSortDirChange } = props;
  const { t } = useTranslation("projects");
  const [open, setOpen] = useState(false);
  const isActive = sortFieldKey !== "none";
  const label = isActive ? sortFieldLabel(t, sortFieldKey, dimensions) : t("sort.default");
  const isPrimaryDir = sortOrderOptions(t, sortFieldKey)[0].dir === sortDir;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <AppButton
          type="button"
          appearance="outlined"
          color="zinc150"
          data-testid="sort-trigger"
          className="h-8 shrink-0 gap-1 !px-3 text-[14px] font-medium !text-innoveraZinc-900 shadow-none [&_svg]:text-innoveraZinc-900"
        >
          <ArrowsDownUp className="h-4 w-4 shrink-0 text-zinc-600" />
          <span className="truncate max-w-[160px]">{label}</span>
          {isActive ? (
            isPrimaryDir ? (
              <ArrowDown className="h-4 w-4 shrink-0 text-zinc-600" />
            ) : (
              <ArrowUp className="h-4 w-4 shrink-0 text-zinc-600" />
            )
          ) : null}
        </AppButton>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-[220px] rounded-lg border-0 bg-white p-0 shadow-md ring-1 ring-inset ring-zinc-100"
      >
        <SortBySection
          sortFieldKey={sortFieldKey}
          sortDir={sortDir}
          dimensions={dimensions}
          onSortFieldChange={onSortFieldChange}
          onSortDirChange={onSortDirChange}
        />
      </PopoverContent>
    </Popover>
  );
}
